import { useMsal } from "@azure/msal-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Fragment, useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import {
  useGetEquipmentHierarchy,
  useRootEquipmentApi,
} from "../../api/equipment-api";
import { useGetSiteApi } from "../../api/site-api";
import {
  createSupportTicketApi,
  useCallbackTestAllowedApi,
  useSupportLineTermsApi,
  useSupportTicketEquipmentApi,
  useValidContractsApi,
} from "../../api/support-line-api";
import checkIcon from "../../assets/icons/check.svg";
import headphonesIcon from "../../assets/icons/headphones2-1.svg";
import CountryCodes from "../../config/country_prefixes.json";
import { titles } from "../../constants/titles";
import { ProductionArea } from "../../enums/production-area.enum";
import { useSelectedSites } from "../../hooks";
import type { SiteData } from "../../models/site-data";
import {
  SupportTicketStatus,
  SupportTicketType,
} from "../../models/support-line";
import AdditionalDetailsDialog from "../AdditionalDetailsDialog/AdditionalDetailsDialog";
import AttachFileDialog from "../AttachFileDialog/AttachFileDialog";
import Button from "../Button/Button";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import Icon from "../Icon";
import styles from "./Support.module.css";
import SupportLineTerms from "./SupportLineTerms/SupportLineTerms";

type SupportProps = {
  onDismiss: () => void;
};

type FormValues = {
  phone: string;
  email: string;
  mine: string;
  mill: string; // single select
  equipment: string;
  subject: string;
  eventDateTime: string;
  contractNumber?: string | null;
  description?: string | null;
  country?: string;
  isCallbackTest?: boolean | null;
};

type CountryCode = { name: string; code: string; prefix: string };

const schema: Yup.ObjectSchema<FormValues> = Yup.object({
  phone: Yup.string()
    .required("Telephone is required")
    .test(
      "not-blank",
      "Telephone is required",
      (v) => !!v && v.trim().length > 0
    )
    .min(4, "Phone must be 4–20 characters")
    .max(20, "Phone must be 4–20 characters")
    .matches(/^[0-9+\s-]+$/, "Use digits, spaces, dashes and + only"),
  email: Yup.string()
    .trim()
    .transform((v) => (v === "" ? undefined : v))
    .max(254, "Email must be at most 254 characters")
    .matches(
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
      "Enter a valid email (e.g. name@example.com)"
    )
    .required("Email is required"),
  mine: Yup.string().required("Mine is required"),
  mill: Yup.string().required("Mill is required"), // single select
  equipment: Yup.string().required("Equipment is required"),
  subject: Yup.string()
    .required("Subject is required")
    .max(100, "Max 100 characters"),
  eventDateTime: Yup.string()
    .required("Date & time is required")
    .test("valid-datetime", "Invalid date/time", (v) => {
      if (!v) return false;
      const parts = v.split(" ");
      if (parts.length !== 2) return false;
      return !isNaN(Date.parse(parts[0])) && /^\d{2}:\d{2}$/.test(parts[1]);
    }),
  contractNumber: Yup.string().nullable().notRequired(),
  description: Yup.string().nullable().max(2000, "Max 2000 characters"),
  country: Yup.string().optional(),
  isCallbackTest: Yup.boolean().notRequired(),
});

type SupportEntryMode = "ticket" | "supportLine";

const Support = ({ onDismiss }: SupportProps) => {
  // A case belongs to exactly one site. With several selected the user picks
  // one below; with a single selection it is pinned automatically.
  const { selectedSites } = useSelectedSites();
  const showSitePicker = selectedSites.length > 1;
  const [pickedSite, setPickedSite] = useState<string>(
    selectedSites.length === 1 ? selectedSites[0] : ""
  );

  useEffect(() => {
    setPickedSite((current) =>
      current && selectedSites.includes(current)
        ? current
        : selectedSites.length === 1
          ? selectedSites[0]
          : ""
    );
  }, [selectedSites]);

  const site = pickedSite;

  // Toggle between options and form inside same modal
  const [showSupportTicketForm, setShowSupportTicketForm] = useState(false);
  const [entryMode, setEntryMode] = useState<SupportEntryMode>("ticket");
  const [submittedPhone, setSubmittedPhone] = useState<string>("");
  const [showTerms, setShowTerms] = useState(false);

  const openTicketForm = () => {
    setEntryMode("ticket");
    setShowSupportTicketForm(true);
  };

  const openSupportLineForm = () => {
    setEntryMode("supportLine");
    setShowSupportTicketForm(true);
  };

  const closeForm = () => {
    setShowTerms(false);
    setShowSupportTicketForm(false);
  };

  const uiText = useMemo(() => {
    if (entryMode === "supportLine") {
      return {
        formTitle: "Contact 24/7 SupportLine",
        formIntro:
          "In order to help you quicker, we need some information before a SupportLine agent contacts you.",
        successTitle: "Thank you!",
        successBody: `Your SupportLine request has been received correctly. A SupportLine agent will call you shortly on ${submittedPhone}.`,
      };
    }

    return {
      formTitle: "Create ticket",
      formIntro: undefined,
      successTitle: "Thank you!",
      successBody:
        "An engineering ticket has been successfully submitted. Our team will get back to you as soon as possible.",
    };
  }, [entryMode, submittedPhone]);

  // Identity (MSAL)
  const { accounts } = useMsal();
  const username = accounts.length > 0 ? accounts[0].name : "";
  const primaryEmail = accounts.length > 0 ? (accounts[0].username ?? "") : "";

  // Data
  const { data: rootEquipment } = useRootEquipmentApi(site);
  const { data: equipmentHierarchy } = useGetEquipmentHierarchy(!!site);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isAttachOpen, setIsAttachOpen] = useState(false);

  // Country codes and defaults
  const countryCodes: CountryCode[] = useMemo(
    () =>
      [...(CountryCodes as CountryCode[])].sort((a, b) =>
        a.code.localeCompare(b.code)
      ),
    []
  );

  function findCountryByPrefix(
    phoneValue: string,
    countryCodes: CountryCode[]
  ): string | undefined {
    const trimmed = phoneValue.trim();
    if (!trimmed) return undefined;
    const matches = countryCodes
      .filter((c) => trimmed.startsWith(c.prefix))
      .sort((a, b) => b.prefix.length - a.prefix.length);
    return matches[0]?.code;
  }

  // Recursively find the site's countryCode within the transformed hierarchy (SiteGroup[])
  function findCountryCodeInHierarchy(
    hierarchy: unknown,
    siteName: string
  ): string | undefined {
    const visited = new Set<any>();

    const isSiteData = (n: any): n is SiteData =>
      n &&
      typeof n === "object" &&
      typeof n.name === "string" &&
      typeof n.countryCode === "string";

    const dfs = (node: any): string | undefined => {
      if (!node || visited.has(node)) return undefined;
      visited.add(node);

      if (Array.isArray(node)) {
        for (const item of node) {
          const hit = dfs(item);
          if (hit) return hit;
        }
        return undefined;
      }

      if (typeof node === "object") {
        if (isSiteData(node)) {
          if ((node.name ?? "").toLowerCase() === siteName.toLowerCase()) {
            return node.countryCode;
          }
        }
        for (const key of Object.keys(node)) {
          const hit = dfs(node[key]);
          if (hit) return hit;
        }
      }
      return undefined;
    };

    return dfs(hierarchy);
  }

  // Derive country code for current site from equipmentHierarchy (SiteGroup[])
  const derivedCountryCode = useMemo(() => {
    if (!equipmentHierarchy || !site) return undefined;
    return findCountryCodeInHierarchy(equipmentHierarchy, site);
  }, [equipmentHierarchy, site]);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitted },
    watch,
    setValue,
    control,
    reset,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      country: "CH", // will be overridden by derivedCountryCode when available
      phone: "", // will be set to country prefix when available
      mine: site,
      mill: "", // single select
      equipment: "",
      email: primaryEmail,
      description: "",
      contractNumber: "unknown",
      eventDateTime: `${new Date().toISOString().split("T")[0]} 00:00`,
      isCallbackTest: false,
    },
  });

  // When hierarchy resolves, set country and phone prefix if phone empty
  useEffect(() => {
    if (!derivedCountryCode) return;
    const currentCountry = getValues("country");
    const currentPhone = (getValues("phone") ?? "").trim();
    // Update country if different
    if (currentCountry !== derivedCountryCode) {
      setValue("country", derivedCountryCode, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
    // Set phone prefix if empty
    if (!currentPhone) {
      const prefix =
        countryCodes.find((c) => c.code === derivedCountryCode)?.prefix ??
        "+41";
      setValue("phone", prefix, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [derivedCountryCode, countryCodes, getValues, setValue]);

  const descriptionValue = watch("description");
  const selectedCountry = watch("country");
  const phonePlaceholder = useMemo(
    () => countryCodes.find((c) => c.code === selectedCountry)?.prefix ?? "+41",
    [selectedCountry, countryCodes]
  );
  const selectedMill = watch("mill") ?? ""; // single select
  const { data: siteData, isError: siteError } = useGetSiteApi(site);
  const productTitle = siteData?.productionArea
    ? `${titles[siteData.productionArea as keyof typeof titles] ?? siteData.productionArea}`
    : "Grinding Connect";
  const isHoisting = siteData?.productionArea === ProductionArea.Hoisting;
  const {
    data: equipment,
    isLoading: equipmentLoading,
    isError: equipmentError,
  } = useSupportTicketEquipmentApi(
    site,
    selectedMill,
    siteData?.productionArea
  );

  const showPhoneError = (touchedFields.phone || isSubmitted) && !!errors.phone;

  // Load valid contracts for selected mill
  const {
    data: contracts = [],
    isLoading: contractsLoading,
    isError: contractsError,
  } = useValidContractsApi(site, selectedMill);

  const { data: activeTerms, isLoading: termsLoading } = useSupportLineTermsApi(
    site,
    selectedMill,
    entryMode === "supportLine"
  );

  const termsRequired =
    entryMode === "supportLine" &&
    !!selectedMill &&
    !!activeTerms &&
    !contractsLoading &&
    !contractsError &&
    contracts.length === 0;

  // Only relevant for the SupportLine entry mode; permission is re-checked server-side.
  const { data: showCallbackTestOption } = useCallbackTestAllowedApi(
    entryMode === "supportLine" ? site : undefined,
    entryMode === "supportLine" ? selectedMill : undefined
  );

  useEffect(() => {
    // Reset equipment and contract when mill changes
    setValue("equipment", "", { shouldDirty: false, shouldValidate: false });
    setValue("contractNumber", "unknown", {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [site, selectedMill, setValue]);

  useEffect(() => {
    // Mills belong to a site, so switching site invalidates the chosen mill.
    setValue("mine", site, { shouldDirty: false, shouldValidate: false });
    setValue("mill", "", { shouldDirty: false, shouldValidate: false });
  }, [site, setValue]);

  // Details dialog
  const openDetails = () => {
    setDetailsDraft(descriptionValue ?? "");
    setIsDetailsOpen(true);
  };

  // Files
  const onPickFiles = () => setIsAttachOpen(true);

  // Success dialog state
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const submitTicket = async (data: FormValues, termsVersion?: string) => {
    setIsSubmitting(true);
    try {
      // Build form-data matching SupportTicketRequest ([FromForm])
      const formData = new FormData();

      const ticketType =
        entryMode === "supportLine"
          ? SupportTicketType.SupportLineCase
          : SupportTicketType.EngineeringTicket;

      formData.append("Type", ticketType.toString());
      formData.append("ContactEmail", data.email);
      formData.append("ContactPhone", data.phone);
      formData.append("Subject", data.subject);
      formData.append("Equipment", data.equipment);
      formData.append("Status", SupportTicketStatus.Open.toString());

      // Parse combined date-time value
      const [datePart, timePart] = data.eventDateTime.split(" ");
      const eventDateTime = `${datePart}T${timePart}:00`;
      formData.append("EventDateTime", eventDateTime);

      if (data.contractNumber)
        formData.append("ContractNumber", data.contractNumber);
      if (data.description) formData.append("Description", data.description);
      if (entryMode === "supportLine")
        formData.append("IsCallbackTest", String(Boolean(data.isCallbackTest)));
      if (termsVersion) {
        formData.append("TermsAndConditionsAccepted", "true");
        formData.append("TermsAndConditionsVersion", termsVersion);
      }
      files.forEach((f) => formData.append("Attachments", f));

      const customer = site;
      const rootEquipment = selectedMill; // first selected mill
      await createSupportTicketApi(customer, rootEquipment, formData);

      setSubmittedPhone((data.phone ?? "").trim());
      setFiles([]);
      reset();
      setShowTerms(false);
      setIsSuccessOpen(true);
    } catch (err) {
      console.error("Support ticket submit failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => submitTicket(data);

  const onFormSubmit = handleSubmit(
    termsRequired ? () => setShowTerms(true) : onSubmit
  );

  const onAcceptTerms = (termsVersion: string) =>
    handleSubmit(
      (data) => submitTicket(data, termsVersion),
      () => setShowTerms(false)
    )();

  const supportLogUrl = site
    ? `/support-line/support?sites=${encodeURIComponent(site)}`
    : "/support-line/support";

  return (
    <Fragment>
      <div className={styles.modal}>
        <div className={styles["modal-content"]}>
          <div className={styles["modal-header"]}>
            <div className={styles["modal-logo-wrap"]}>
              <span className={styles["modal-logo-circle"]}>
                <img
                  src={headphonesIcon}
                  alt="Headphones"
                  className={"modal-logo-icon"}
                />
              </span>
            </div>
            <div>
              <h5 className={styles["modal-title"]}>{productTitle}</h5>
              <h3 className={styles["modal-subtitle"]}>Support</h3>
            </div>
          </div>

          {/* BODY: success | form | options */}
          {isSuccessOpen ? (
            // Success body inside the same modal
            <div className={styles["modal-body"]}>
              <div className={styles["modal-options-container"]}>
                <p className={styles["ticket-title"]}>{uiText.successTitle}</p>

                <div className={styles["modal-option"]}>
                  <span className={styles["modal-logo-icon"]}>
                    <img
                      src={checkIcon}
                      alt="Successful check"
                      className={"modal-logo-icon"}
                    />
                  </span>
                  <p>{uiText.successBody}</p>
                </div>

                <div className={styles["modal-option"]}>
                  <p>
                    You can find all your tickets and support cases in{" "}
                    {/* FIXME: hardcoded url*/}
                    <a href={supportLogUrl}>
                      <b>
                        <u>Support log</u>
                      </b>
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          ) : !showSupportTicketForm ? (
            // Options view
            <div className={styles["modal-body"]}>
              <div className={styles["modal-options-container"]}>
                <div className={styles["modal-option"]}>
                  <p>
                    <b>
                      For general inquiries or less critical issues
                      {siteData?.productionArea === ProductionArea.Hoisting &&
                        " with Smart Hoisting"}
                    </b>
                    , create a support ticket and our team will get back to you
                    as soon as possible.
                  </p>
                  <Button
                    variant="secondary"
                    text="Create Ticket"
                    icon="Envelope"
                    onClick={openTicketForm}
                  />
                </div>

                <div
                  className={`${styles["modal-option"]} ${isHoisting ? styles["modal-option-disabled"] : ""}`}
                  aria-disabled={isHoisting}
                >
                  <p>
                    <b>
                      For critical issues or urgent assistance
                      {isHoisting && " with Smart Hoisting"}
                    </b>
                    , connect with an expert through our 24/7 SupportLine.
                  </p>
                  <Button
                    variant="brand"
                    text="24/7 SupportLine"
                    icon="Call"
                    onClick={openSupportLineForm}
                    disabled={isHoisting}
                  />
                </div>

                {/* FIXME: hardcoded url */}
                <a href={supportLogUrl} className={styles["support-log-card"]}>
                  <Icon name="List" className={styles["support-log-icon"]} />
                  <div className={styles["support-log-text"]}>
                    <p className={styles["support-log-title"]}>Support Log</p>
                    <p className={styles["support-log-subtitle"]}>
                      View and track all your support cases
                    </p>
                  </div>
                  <span className={styles["support-log-action"]}>
                    View all cases
                  </span>
                </a>
              </div>
            </div>
          ) : showTerms ? (
            <div className={styles["modal-body"]}>
              <SupportLineTerms
                customer={site}
                rootEquipment={selectedMill}
                isSubmitting={isSubmitting}
                onBack={() => setShowTerms(false)}
                onAccept={onAcceptTerms}
              />
            </div>
          ) : (
            // Ticket form view (shared for both entry modes)
            <div className={styles["modal-body"]}>
              <div>
                <p className={styles["ticket-title"]}>{uiText.formTitle}</p>
                {uiText.formIntro && (
                  <p
                    className={styles["ticket-intro"]}
                    style={{ marginTop: 0 }}
                  >
                    {uiText.formIntro}
                  </p>
                )}

                {termsRequired && (
                  <div className={styles["terms-banner"]} role="note">
                    <div className={styles["terms-banner-header"]}>
                      <Icon
                        name="Information_circle_2"
                        className={styles["terms-banner-icon"]}
                      />
                      <p className={styles["terms-banner-title"]}>
                        Terms and conditions needed
                      </p>
                    </div>
                    <p className={styles["terms-banner-text"]}>
                      No active service agreement has been identified for the
                      requested support services. As a precondition to receiving
                      expert remote support, you will be required to review and
                      accept ABB’s applicable Terms and Conditions, which set
                      out the service scope, responsibilities, limitations of
                      liability, and applicable service rates.
                    </p>
                    <button
                      type="button"
                      className={styles["terms-banner-link"]}
                      onClick={() => setShowTerms(true)}
                    >
                      Read terms now
                    </button>
                  </div>
                )}

                <div className={styles["ticket-panel"]}>
                  <div className={styles["ticket-grid"]}>
                    {/* Left card - Contact Info */}
                    <div className={styles["ticket-card-left"]}>
                      <p className={styles["ticket-card-title"]}>
                        Contact Info
                      </p>
                      <div className={styles["ticket-card-body-left"]}>
                        <div
                          className={styles["user-info-grid"]}
                          aria-label="Customer and User"
                        >
                          <div>
                            <p className={styles["muted-label"]}>Customer</p>
                            <p className={styles["strong-value"]}>
                              {site || "-"}
                            </p>
                          </div>
                          <div>
                            <p className={styles["muted-label"]}>User</p>
                            <p className={styles["strong-value"]}>
                              {username || "-"}
                            </p>
                          </div>
                        </div>

                        <div className={styles["input-container"]}>
                          <label
                            htmlFor="phone"
                            className={styles["field-label"]}
                          >
                            Telephone <span className={styles.req}>*</span>
                          </label>
                          <div
                            className={styles["inline-two"]}
                            aria-invalid={!!errors.phone}
                          >
                            <Controller
                              name="country"
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <select
                                  id="country"
                                  className={styles["input-country"]}
                                  value={value ?? ""}
                                  onChange={(e) => {
                                    const code = e.target.value;
                                    onChange(code);
                                    const selected = countryCodes.find(
                                      (c) => c.code === code
                                    );
                                    if (selected) {
                                      setValue("phone", selected.prefix, {
                                        shouldDirty: true,
                                        // Avoid showing min-length error just from selecting a prefix.
                                        shouldValidate:
                                          isSubmitted || !!touchedFields.phone,
                                      });
                                    }
                                  }}
                                >
                                  {countryCodes.map((c) => (
                                    <option key={c.code} value={c.code}>
                                      {c.code}
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                            <input
                              id="phone"
                              type="tel"
                              inputMode="numeric"
                              pattern="[0-9+\-\s]*"
                              maxLength={20}
                              placeholder={phonePlaceholder}
                              className={`${styles.input} ${errors.phone ? styles.error : ""}`}
                              {...register("phone", {
                                onChange: (e) => {
                                  const code = findCountryByPrefix(
                                    e.target.value,
                                    countryCodes
                                  );
                                  if (code) {
                                    setValue("country", code, {
                                      shouldDirty: true,
                                      shouldValidate: false,
                                    });
                                  }
                                },
                              })}
                            />
                          </div>
                          <div className={styles["field-info"]}>
                            <p>Support may call this number</p>
                            <p className={styles["error-text"]}>
                              {showPhoneError ? errors.phone?.message : ""}
                            </p>
                          </div>
                        </div>

                        <div className={styles["input-container"]}>
                          <label
                            htmlFor="email"
                            className={styles["field-label"]}
                          >
                            Email <span className={styles.req}>*</span>
                          </label>
                          <input
                            id="email"
                            type="email"
                            placeholder="email@email.com"
                            className={`${styles.input} ${errors.email ? styles.error : ""}`}
                            {...register("email")}
                          />
                          <p className={styles["error-text"]}>
                            {errors.email?.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right card - Ticket details (all fields) */}
                    <div className={styles["ticket-card-right"]}>
                      <p className={styles["ticket-card-title"]}>
                        Ticket details
                      </p>
                      <div className={styles["ticket-card-body-right"]}>
                        <div className={styles["ticket-card-body-right-row"]}>
                          {/* Only when several sites are selected; a case
                              belongs to one site, and it drives the mill list. */}
                          {showSitePicker && (
                            <div className={styles["input-container"]}>
                              <label
                                htmlFor="mine"
                                className={styles["field-label"]}
                              >
                                Site <span className={styles.req}>*</span>
                              </label>
                              <select
                                id="mine"
                                className={`${styles.input} ${styles.select} ${errors.mine ? styles.error : ""}`}
                                value={site}
                                onChange={(e) => setPickedSite(e.target.value)}
                                aria-invalid={!!errors.mine}
                              >
                                <option value="" disabled>
                                  Select site
                                </option>
                                {selectedSites.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                              <p className={styles["error-text"]}>
                                {errors.mine?.message as string}
                              </p>
                            </div>
                          )}

                          <div className={styles["input-container"]}>
                            <label
                              htmlFor="mill"
                              className={styles["field-label"]}
                            >
                              {siteData?.productionArea ===
                              ProductionArea.Hoisting
                                ? "Hoist"
                                : "Mill"}{" "}
                              <span className={styles.req}>*</span>
                            </label>
                            <Controller
                              name="mill"
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <select
                                  id="mill"
                                  className={`${styles.input} ${styles.select} ${errors.mill ? styles.error : ""}`}
                                  value={value ?? ""}
                                  onChange={(e) => onChange(e.target.value)}
                                  aria-invalid={!!errors.mill}
                                >
                                  <option value="" disabled>
                                    {siteData?.productionArea ===
                                    ProductionArea.Hoisting
                                      ? "Select hoist"
                                      : "Select mill"}
                                  </option>
                                  {rootEquipment?.map((mill) => (
                                    <option key={mill} value={mill}>
                                      {mill}
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                            <p className={styles["error-text"]}>
                              {errors.mill?.message as string}
                            </p>
                          </div>

                          <div className={styles["input-container"]}>
                            <label
                              htmlFor="equipment"
                              className={styles["field-label"]}
                            >
                              Equipment <span className={styles.req}>*</span>
                            </label>
                            <select
                              id="equipment"
                              className={`${styles.input} ${styles.select}`}
                              {...register("equipment")}
                              disabled={!equipment?.length}
                            >
                              <option value="" disabled>
                                {equipmentLoading
                                  ? "Loading equipment..."
                                  : "Select equipment"}
                              </option>
                              {equipment?.map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                            <p className={styles["error-text"]}>
                              {siteError || equipmentError
                                ? "Unable to load equipment for this production area. Please reopen the form to retry."
                                : errors.equipment?.message}
                            </p>
                          </div>

                          <div className={styles["input-container"]}>
                            <label
                              htmlFor="subject"
                              className={styles["field-label"]}
                            >
                              Subject <span className={styles.req}>*</span>
                            </label>
                            <textarea
                              id="subject"
                              placeholder="Briefly describe the issue"
                              rows={2}
                              maxLength={100}
                              className={`${styles["input-textarea"]} ${errors.subject ? styles.error : ""}`}
                              {...register("subject")}
                            />
                            <p className={styles["char-counter"]}>
                              {(watch("subject") ?? "").length} / 100
                            </p>
                            <p className={styles["error-text"]}>
                              {errors.subject?.message}
                            </p>
                          </div>
                        </div>

                        <div className={styles["ticket-card-body-right-row"]}>
                          <div className={styles["input-container"]}>
                            <label
                              htmlFor="eventDateTime"
                              className={styles["field-label"]}
                            >
                              Date & time the event occurred{" "}
                              <span className={styles.req}>*</span>
                            </label>
                            <Controller
                              name="eventDateTime"
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <DateTimePicker
                                  value={value}
                                  onChange={onChange}
                                  placeholder="Select date & time"
                                  error={!!errors.eventDateTime}
                                />
                              )}
                            />
                            <p className={styles["error-text"]}>
                              {errors.eventDateTime?.message}
                            </p>
                          </div>
                          <div className={styles["input-container"]}>
                            <label
                              htmlFor="contractNumber"
                              className={styles["field-label"]}
                            >
                              ABB Contract number
                            </label>
                            <select
                              id="contractNumber"
                              className={`${styles.input} ${styles.select}`}
                              {...register("contractNumber")}
                              disabled={!selectedMill || contractsLoading}
                            >
                              {!selectedMill && (
                                <option value="" disabled>
                                  Select mill first
                                </option>
                              )}
                              {selectedMill && contractsLoading && (
                                <option value="" disabled>
                                  Loading contracts...
                                </option>
                              )}
                              {selectedMill &&
                                !contractsLoading &&
                                contractsError && (
                                  <option value="" disabled>
                                    Failed to load contracts
                                  </option>
                                )}
                              {selectedMill &&
                                !contractsLoading &&
                                !contractsError &&
                                contracts.length === 0 && (
                                  <option value="" disabled>
                                    No valid contracts found
                                  </option>
                                )}
                              <option value="unknown">Unknown</option>
                              {selectedMill &&
                                !contractsLoading &&
                                !contractsError &&
                                contracts.map((c) => (
                                  <option
                                    key={c.contractId}
                                    value={c.abbContractNumber}
                                  >
                                    {c.abbContractNumber}
                                  </option>
                                ))}
                            </select>
                            <p className={styles["error-text"]}>
                              {errors.contractNumber?.message}
                            </p>
                          </div>

                          {entryMode === "supportLine" &&
                            showCallbackTestOption && (
                              <div className={styles["input-container"]}>
                                <label
                                  htmlFor="isCallbackTest"
                                  className={styles["field-label"]}
                                >
                                  <input
                                    id="isCallbackTest"
                                    type="checkbox"
                                    {...register("isCallbackTest")}
                                  />{" "}
                                  Callback test
                                </label>
                              </div>
                            )}

                          <div className={styles["row-between"]}>
                            <div>
                              <p className={styles["mini-title"]}>
                                Additional details
                              </p>
                              <p className={styles.hint}>
                                {descriptionValue
                                  ? `${descriptionValue.length} characters added`
                                  : "Click to add"}
                              </p>
                            </div>
                            <Button
                              variant="transparent"
                              size="small"
                              icon="Edit"
                              aria-label="Add additional details"
                              onClick={openDetails}
                            />
                          </div>

                          <div className={styles["row-between"]}>
                            <div>
                              <p className={styles["mini-title"]}>Picture</p>
                              <p className={styles.hint}>
                                {files.length > 0
                                  ? `${files.length} files attached`
                                  : "Attach files"}
                              </p>
                            </div>
                            <div>
                              <Button
                                variant="transparent"
                                size="small"
                                icon="Paper_clip"
                                aria-label="Attach files"
                                onClick={onPickFiles}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer actions inside ticket form */}
                <div className={styles["form-actions"]}>
                  <div className={styles["form-actions-left"]}>
                    <Button variant="ghost" text="Cancel" onClick={onDismiss} />
                    <Button
                      variant="transparent"
                      text="Back"
                      onClick={closeForm}
                    />
                  </div>
                  <div className={styles["form-actions-right"]}>
                    <Button
                      variant={termsRequired ? "primary" : "secondary"}
                      type="submit"
                      form="ticketForm"
                      text={
                        isSubmitting
                          ? "Submitting..."
                          : termsRequired
                            ? "Review terms and submit"
                            : "Submit ticket"
                      }
                      onClick={onFormSubmit}
                      disabled={
                        isSubmitting ||
                        !equipment?.length ||
                        (entryMode === "supportLine" &&
                          (contractsLoading || termsLoading))
                      }
                    />
                  </div>
                </div>

                {/* Hidden form element to bind submit and validation */}
                <form
                  id="ticketForm"
                  onSubmit={onFormSubmit}
                  noValidate
                  aria-hidden
                  style={{ display: "none" }}
                />

                {/* Details dialog */}
                <AdditionalDetailsDialog
                  open={isDetailsOpen}
                  value={detailsDraft}
                  onChange={setDetailsDraft}
                  onClose={() => setIsDetailsOpen(false)}
                  onSave={() => {
                    setValue("description", detailsDraft, {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                    setIsDetailsOpen(false);
                  }}
                />

                {/* Attach files dialog */}
                {isAttachOpen && (
                  <AttachFileDialog
                    existingFiles={files}
                    onDismiss={() => setIsAttachOpen(false)}
                    onSubmit={(selected) => {
                      const seen = new Set<string>();
                      const unique = selected.filter((f) => {
                        const key = `${f.name}:${f.size}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      });
                      setFiles(unique);
                      setIsAttachOpen(false);
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* FOOTER: options cancel | success actions */}
          {!isSuccessOpen && !showSupportTicketForm && (
            <div className={styles["modal-footer"]}>
              <Button variant="ghost" text="Cancel" onClick={onDismiss} />
            </div>
          )}

          {isSuccessOpen && (
            <div
              className={`${styles["modal-footer"]} ${styles["modal-footer-center"]}`}
            >
              <Button
                variant="secondary"
                text="Done"
                onClick={() => {
                  setIsSuccessOpen(false);
                  onDismiss();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default Support;
